import i18n from '@/plugins/i18n';
import * as types from './mutations-type.js';
import PreRecordsService from "../../../services/PreRecordsService";
import StorageTypesService from "../../../services/StorageTypesService";
import StructuresService from "../../../services/StructuresService";
import Locations from "@/services/Locations";
import AppointmentsService from "../../../services/AppointmentsService";

export default {
    loadingON({commit, state}) {
        commit(types.SET_LOADING_ON, true);
    },
    loadingOFF({commit}) {
        commit(types.SET_LOADING_OFF, false);
    },
    async findPreRecordById({dispatch, commit, state}, [id, toast]) {
        dispatch('loadingON');
        try {
            const {data} = await PreRecordsService.recordById(id);
            commit(types.SET_PRE_RECORD, data);
            
            // Set advancedConfig directly from response
            if (data.advancedConfig) {
                commit(types.SET_PRE_RECORD_CONFIG, data.advancedConfig);
            }
            
            dispatch('loadingOFF');
        } catch (error) {
            if (error.message !== 'cancelRequest')
                toast.error(i18n.t('pre-records.notification.listError'), {
                    icon: "mdi-alert-circle",
                    queueable: true
                });
        }
    },
    async fetchStorageTypesActiveByCompany({dispatch, commit, state}, [profile, toast]) {
        try {
            const company = profile.company_id;
            const {data} = await StorageTypesService.storageTypesActiveByCompany(company);
            commit(types.SET_STORAGE_TYPE_FOR_RECORD_LIST, data);
        } catch (error) {
            if (error.message !== 'cancelRequest')
                toast.error(i18n.t('storageType.notification.listError'), {
                    icon: "mdi-alert-circle",
                    queueable: true
                });
        }
    },
    async fetchListAppointmentsByCompanyPaginated({dispatch, commit, state}, [paginationAndFilter, source, toast]) {
        state.recordsByCompany = [];
        try {
            const {data} = await PreRecordsService.appointmentsByCompanyPaginated(paginationAndFilter, source);
            commit(types.SET_APPOINTMENTS_BY_COMPANY_PAGINATED, data);
        } catch (error) {
            if (error.code !== 'ERR_CANCELED')
                toast.error(i18n.t('pre-records.notification.listError'), {
                    icon: "mdi-alert-circle",
                    queueable: true
                });
        }
    },
    async fetchListPreRecordsByCompanyFiltered({dispatch, commit, state}, [pageableDTO, toast]) {
        dispatch('loadingON');
        try {
            const {data} = await PreRecordsService.preRecordsByCompanyFiltered(pageableDTO);
            commit(types.SET_APPOINTMENTS_BY_COMPANY_FILTERED, data);
            dispatch('loadingOFF');
        } catch (error) {
            if (error.message !== 'cancelRequest')
                toast.error(i18n.t('pre-records.notification.listError'), {
                    icon: "mdi-alert-circle",
                    queueable: true
                });
        }
    },
    async saveRecord({dispatch, commit, state}, [record, document, documents, editOrSave, toast]) {
        dispatch('loadingON');
        try {
            await PreRecordsService.save(record, document, documents, editOrSave);
            toast.success(i18n.t('pre-records.notification.save'), {
                icon: 'mdi-check-circle',
                queueable: true,
            });
        } catch (error) {
            if (error.message.includes('406')) {
                toast.info(i18n.t('pre-records.notification.inUse'), {
                    icon: "mdi-alert-circle",
                    queueable: true
                });
            } else {
                if (error.message !== 'cancelRequest')
                    toast.error(i18n.t('pre-records.notification.saveError'), {
                        icon: "mdi-alert-circle",
                        queueable: true
                    });
            }
        } finally {
            dispatch('loadingOFF');
        }
    },
    async deleteRecord({commit, state}, [id, toast]) {
        try {
            await PreRecordsService.delete(id);
            toast.success(i18n.t('pre-records.notification.deleted'), {
                icon: 'mdi-check-circle',
                queueable: true,
            });
        } catch (error) {
            if (error.message.includes('406')) {
                toast.info(i18n.t('pre-records.notification.inUse'), {
                    queueable: true,
                    icon: "mdi-alert-circle",
                });
            } else {
                if (error.message !== 'cancelRequest')
                    toast.error(i18n.t('pre-records.notification.deletedError'), {
                        icon: "mdi-alert-circle",
                        queueable: true
                    });
            }
        }
    },
    async fetchListStructures({commit, state}, [profile, toast]) {
        try {
            const company = profile.company_id;
            const {data} = await StructuresService.structuresValues(company);
            commit(types.SET_STRUCTURES_LIST, data);
        } catch (error) {
            if (error.message !== 'cancelRequest')
                toast.error(i18n.t('pre-records.notification.listError'), {
                    icon: "mdi-alert-circle",
                    queueable: true
                });
        }
    },
    async changeMobileActive({dispatch, commit, state}, [company, id, status, toast]) {
        dispatch('loadingON');
        try {
            await PreRecordsService.changeMobileActive(company, id, status);
        } catch (error) {
            if (error.message.includes('406')) {
                toast.info(i18n.t('pre-records.notification.inUse'), {
                    queueable: true,
                    icon: "mdi-alert-circle",
                });
            } else {
                if (error.message !== 'cancelRequest')
                    toast.error(i18n.t('pre-records.notification.saveError'), {
                        icon: "mdi-alert-circle",
                        queueable: true
                    });
            }
        }
    },
    async updatePreRecordConfig({dispatch, commit, state}, [config, toast]) {
        try {
            // Actualizar solo el estado local, ya que advancedConfig es parte del preRecord
            commit(types.SET_PRE_RECORD_CONFIG, config);
            
            toast.success(i18n.t('pre-records.notification.configUpdated'), {
                icon: 'mdi-check-circle',
                queueable: true,
            });
            
            return Promise.resolve(config);
        } catch (error) {
            toast.error(i18n.t('pre-records.notification.configUpdateError'), {
                icon: "mdi-alert-circle",
                queueable: true
            });
            
            return Promise.reject(error);
        }
    },
    async fetchPreRecordConfig({dispatch, commit, state}, [processId, toast]) {
        dispatch('loadingON');
        try {
            const {data} = await PreRecordsService.getConfigByProcess(processId);
            
            if (data) {
                commit(types.SET_PRE_RECORD_CONFIG, data);
                return Promise.resolve(data);
            } else {
                // If no config exists, set default empty config
                const defaultConfig = {
                    locations: {
                        origin: {
                            primary: { alias: [], isHidden: false, isEditable: true },
                            secondary: { alias: [], isHidden: false, isEditable: true }
                        },
                        destination: {
                            primary: { alias: [], isHidden: false, isEditable: true },
                            secondary: { alias: [], isHidden: false, isEditable: true }
                        }
                    },
                    structures: [],
                    groups: []
                };
                commit(types.SET_PRE_RECORD_CONFIG, defaultConfig);
                return Promise.resolve(defaultConfig);
            }
            
        } catch (error) {
            if (error.message !== 'cancelRequest')
                toast.error(i18n.t('pre-records.notification.configFetchError'), {
                    icon: "mdi-alert-circle",
                    queueable: true
                });
            return Promise.reject(error);
        } finally {
            dispatch('loadingOFF');
        }
    },
}
